/**
 * Centralized Realtime channel manager.
 * 
 * Monitors all active Supabase Realtime channels and automatically
 * reconnects channels that enter error or closed states.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { getActiveChannelCount, logActiveChannels } from "@/lib/supabase-channel";

export interface ChannelHealth {
  topic: string;
  state: string;
  isHealthy: boolean;
}

interface RealtimeManagerState {
  channels: ChannelHealth[];
  totalChannels: number;
  healthyChannels: number;
  lastCheck: Date | null;
}

export function useRealtimeManager() {
  const { activeRestaurant } = useRestaurantContext();
  const [state, setState] = useState<RealtimeManagerState>({
    channels: [],
    totalChannels: 0,
    healthyChannels: 0,
    lastCheck: null,
  });

  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCheckingRef = useRef(false);

  // Health check function - monitors all channels and reconnects unhealthy ones
  const performHealthCheck = useCallback(() => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;

    try {
      const allChannels = supabase.getChannels();
      const channelHealthList: ChannelHealth[] = [];
      let healthyCount = 0;
      let reconnectedCount = 0;

      allChannels.forEach((channel) => {
        const state = channel.state;
        // States: "joined", "joining", "leaving", "closed", "errored"
        const isHealthy = state === "joined" || state === "joining";

        channelHealthList.push({
          topic: channel.topic,
          state,
          isHealthy,
        });

        if (isHealthy) {
          healthyCount++;
        } else if (state === "errored" || state === "closed") {
          // Attempt to reconnect unhealthy channels
          console.warn(`[RealtimeManager] Channel ${channel.topic} in ${state} state, attempting reconnect...`);
          try {
            channel.subscribe((status) => {
              if (status === "SUBSCRIBED") {
                console.log(`[RealtimeManager] Channel ${channel.topic} reconnected successfully`);
              }
            });
            reconnectedCount++;
          } catch (error) {
            console.error(`[RealtimeManager] Failed to reconnect ${channel.topic}:`, error);
          }
        }
      });

      if (reconnectedCount > 0) {
        console.log(`[RealtimeManager] Attempted to reconnect ${reconnectedCount} channels`);
      }

      setState({
        channels: channelHealthList,
        totalChannels: allChannels.length,
        healthyChannels: healthyCount,
        lastCheck: new Date(),
      });
    } catch (error) {
      console.error("[RealtimeManager] Error during health check:", error);
    } finally {
      isCheckingRef.current = false;
    }
  }, []);

  // Force reconnect all channels (manual trigger)
  const forceReconnectAll = useCallback(() => {
    console.log("[RealtimeManager] Force reconnecting all channels...");
    const allChannels = supabase.getChannels();
    
    allChannels.forEach((channel) => {
      if (channel.state !== "joined") {
        try {
          channel.subscribe();
        } catch (error) {
          console.error(`[RealtimeManager] Error reconnecting ${channel.topic}:`, error);
        }
      }
    });

    // Recheck after a short delay
    setTimeout(performHealthCheck, 1000);
  }, [performHealthCheck]);

  // Debug function to log all channels
  const debugLogChannels = useCallback(() => {
    logActiveChannels();
  }, []);

  // Setup periodic health checks
  useEffect(() => {
    if (!activeRestaurant) return;

    // Initial check after a short delay (allow channels to initialize)
    const initialCheckTimer = setTimeout(() => {
      performHealthCheck();
    }, 3000);

    // Periodic checks every 15 seconds
    checkIntervalRef.current = setInterval(() => {
      performHealthCheck();
    }, 15000);

    // Listen for online/offline events
    const handleOnline = () => {
      console.log("[RealtimeManager] Network came online, scheduling reconnect...");
      setTimeout(() => {
        forceReconnectAll();
      }, 1000);
    };

    const handleOffline = () => {
      console.log("[RealtimeManager] Network went offline");
      performHealthCheck();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      clearTimeout(initialCheckTimer);
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [activeRestaurant, performHealthCheck, forceReconnectAll]);

  return {
    ...state,
    performHealthCheck,
    forceReconnectAll,
    debugLogChannels,
    getChannelCount: getActiveChannelCount,
  };
}
